import _ from 'lodash';
import {
  matchesBrand,
  matchesCategory,
  matchesStrain,
  matchesVendor,
  matchesProductId,
  matchesInventoryTag,
  matchesProductTag,
} from 'shared/order/bogo/common';

import { isArmageddonDiscountSyncSpecial } from 'shared/helpers/specials-3-5';

export const prepareExcludedProducts = (excluded = {}, ruleKeys = [], productsHash = {}) => {
  const result = {
    _id: excluded._id,
    excludedProducts: {},
    selectedExcludedProducts: {},
  };

  if (excluded?.products && ruleKeys?.products?.length > 0) {
    // handle sale specials
    const saleExclusions = setupExcludedProducts(excluded.products, ruleKeys.products, productsHash);
    result.excludedProducts = { ...(saleExclusions?.excludedProducts || {}) };
    result.selectedExcludedProducts = { ...(saleExclusions?.selectedExcludedProducts || {}) };
  } else {
    // handle offers
    if (ruleKeys?.conditions?.length > 0) {
      const conditionExclusions = setupExcludedProducts(excluded.conditions, ruleKeys.conditions, productsHash);
      result.excludedProducts.conditions = { ...(conditionExclusions?.excludedProducts || {}) };
      result.selectedExcludedProducts.conditions = { ...(conditionExclusions?.selectedExcludedProducts || {}) };
    }

    if (ruleKeys?.rewards?.length > 0) {
      const rewardExclusions = setupExcludedProducts(excluded.rewards, ruleKeys.rewards, productsHash);
      result.excludedProducts.rewards = { ...(rewardExclusions?.excludedProducts || {}) };
      result.selectedExcludedProducts.rewards = { ...(rewardExclusions?.selectedExcludedProducts || {}) };
    }
  }

  return result;
};

export const getConditionName = ({ brandId, brandName, categoryName, productGroup }) => {
  if (productGroup === 'categories') {
    return categoryName;
  }
  if (productGroup === 'brands') {
    return `BRAND__${brandId ?? '*'}__${brandName ?? '*'}`;
  }
  return null;
};

const getExcludedBrandData = (ruleKeys) => {
  const brands = {};
  _.forEach(ruleKeys, (rule) => {
    let brandId;
    let brandName;

    const split = _.split(rule, '__');
    split.shift(); // remove our BRAND_
    brandId = split.shift(); // get our id
    brandName = split.join(); // get our brandName

    // get rid of any junk
    if (brandId === '*') {
      brandId = null;
    }
    if (brandName === '*') {
      brandName = null;
    }

    if (brandId || brandName) {
      brands[brandId ?? brandName] = brandName;
    }
  });
  return brands;
};

const isBrandExclusionMatch = (product, rule = {}) => matchesBrand(rule, product, rule.productGroup, true);

const isCategoryExclusionMatch = (product, rule = {}) => matchesCategory(rule, product, rule.productGroup, true);

const isStrainExclusionMatch = (product, rule = {}) => matchesStrain(rule, product, true);

const isVendorExclusionMatch = (product, rule = {}) => matchesVendor(rule, product, true);

const isActiveBatchTagExclusionMatch = (product, rule = {}) => matchesInventoryTag(rule, product, true);

const isProductTagExclusionMatch = (product, rule = {}) => matchesProductTag(rule, product, true);

const isProductExclusionMatch = (product, exclusion = {}) => {
  const { _id, id, enterpriseProductId } = product;
  const { _id: exclusionId, productIds } = exclusion;
  const productId = _id ?? id;

  const epid = `EPID_${enterpriseProductId}`;
  const productIdMatch = _.includes(_.compact([epid, enterpriseProductId, productId]), exclusionId);
  const productIdsMatch = _.includes(productIds, productId); // v3.5

  return productIdMatch || productIdsMatch || matchesProductId(exclusion, product, true);
};

export const isExcludedProduct = (product, exclusions = [], special = {}) => {
  const checkAllExclusions = isArmageddonDiscountSyncSpecial(special);
  let isExcluded = false;

  _.forEach(exclusions, (exclusion) => {
    if (isProductExclusionMatch(product, exclusion)) {
      isExcluded = true;

      return false;
    }

    if (checkAllExclusions) {
      if (isBrandExclusionMatch(product, exclusion)) {
        isExcluded = true;

        return false;
      }

      if (isCategoryExclusionMatch(product, exclusion)) {
        isExcluded = true;

        return false;
      }

      if (isStrainExclusionMatch(product, exclusion)) {
        isExcluded = true;

        return false;
      }

      if (isVendorExclusionMatch(product, exclusion)) {
        isExcluded = true;

        return false;
      }

      if (isActiveBatchTagExclusionMatch(product, exclusion)) {
        isExcluded = true;

        return false;
      }

      if (isProductTagExclusionMatch(product, exclusion)) {
        isExcluded = true;

        return false;
      }
    }

    return true; // Consistent return
  });

  return isExcluded;
};

const setupExcludedProducts = (excluded = [], ruleKeys = [], productsHash = {}) => {
  const result = {
    excludedProducts: {},
    selectedExcludedProducts: {},
  };

  const isBrand = !!_.find(ruleKeys, (rule) => _.includes(rule, 'BRAND_'));
  const brands = isBrand && getExcludedBrandData(ruleKeys);

  _.forEach(excluded, (item) => {
    let itemKey = item?.key;
    const uncategorizedKey = _.includes(itemKey, 'uncategorized_')
      ? `Uncategorized ${_.split(itemKey, 'uncategorized_')[1]}`
      : itemKey;
    if (!itemKey) {
      return;
    }
    // confirm that this is still an active rule
    let isActiveKey = _.includes(ruleKeys, itemKey) || _.includes(ruleKeys, uncategorizedKey);

    // brands are inconsistent and might be indexed by either id or name
    if (isBrand) {
      const matchesOnBrandName = _.find(_.toPairs(brands), ([_id, name]) => name === itemKey)?.[1];
      let matchesOnBrandId = _.find(_.toPairs(brands), ([id]) => id === itemKey)?.[0];
      // don't accept the brandName in lieu of the id
      matchesOnBrandId = matchesOnBrandId === matchesOnBrandName ? null : matchesOnBrandId;
      if (matchesOnBrandId) {
        itemKey = matchesOnBrandId;
        isActiveKey = true;
      }
      if (matchesOnBrandName) {
        itemKey = matchesOnBrandName;
        isActiveKey = true;
      }
    }

    if (isActiveKey) {
      if (!result.excludedProducts?.[itemKey]) {
        result.excludedProducts[itemKey] = [];
      }

      if (!result.selectedExcludedProducts?.[itemKey]) {
        result.selectedExcludedProducts[itemKey] = {};
      }

      // confirm that this product is still available
      const isActiveProduct = !!productsHash?.[item._id];
      if (isActiveProduct) {
        // update main exclusions list
        result.excludedProducts[itemKey].push({
          __key: item._id,
          Name: item.Name,
        });
        // ensure that this is a selected exclusion
        result.selectedExcludedProducts[itemKey][item._id] = true;
      }
    }
  });

  return result;
};

export default {
  getConditionName,
  isExcludedProduct,
  prepareExcludedProducts,
};
